import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider as MuiThemeProvider, createTheme, ThemeOptions } from "@mui/material/styles";
import { useCookies } from "react-cookie";

import { postUiPreferences } from "../actions";
import { RootState } from "../store";

interface ThemeContextType {
  theme: string;
  muiTheme: ThemeOptions;
  toggleTheme: () => void;
  customColors: Record<string, any>;
  addCustomColor: (key: string, value: any) => void;
  loading: boolean;
}

interface ThemeProviderProps {
  darkThemeOptions: ThemeOptions;
  lightThemeOptions: ThemeOptions;
  children: ReactNode;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined
);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  darkThemeOptions,
  lightThemeOptions,
  children,
}) => {
  const dispatch = useDispatch();
  const authUserId = useSelector((state: RootState) => state.authentication.userId);
  const postUserCall =
    useSelector((state: RootState) => state.apiCalls.postUser["undefined"]) || {};
  const uiPreferences = useSelector(
    (state: RootState) => state.entities.uiPreferences.byUserId[authUserId]
  );

  const [cookies, setCookie, removeCookie] = useCookies(["pref-theme"]);
  const [customColors, setCustomColors] = useState<Record<string, any>>({});
  const [theme, setTheme] = useState<string>(
    (uiPreferences && uiPreferences.theme) || cookies["pref-theme"] || "dark"
  );

  const addCustomColor = (key: string, value: any) => {
    setCustomColors((prev) => ({ ...prev, [key]: value }));
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    removeCookie("pref-theme");
    setCookie("pref-theme", theme);
    document.body.setAttribute("data-bs-theme", theme);

    if (uiPreferences && uiPreferences.theme !== theme) {
      const updatedPreferences = { ...uiPreferences, theme };
      dispatch(postUiPreferences(updatedPreferences));
    }
  }, [theme]);

  useEffect(() => {
    if (uiPreferences) {
      setTheme(uiPreferences.theme);
    }
  }, [uiPreferences]);

  const muiThemeOptions =
    theme === "dark" ? darkThemeOptions : lightThemeOptions;

  const muiThemeOptionsWithCustomColors: ThemeOptions = {
    ...muiThemeOptions,
    palette: {
      ...muiThemeOptions.palette,
      ...customColors,
    },
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        muiTheme: muiThemeOptionsWithCustomColors,
        toggleTheme,
        customColors,
        addCustomColor,
        loading: postUserCall.isFetching,
      }}
    >
      <MuiThemeProvider theme={createTheme(muiThemeOptionsWithCustomColors)}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context)
    throw new Error("useThemeContext must be used within a ThemeProvider");
  return context;
};